import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import type { BroadcastChannel, SelectBroadcastChannelInput } from "../types";

interface ChannelSelectorProps {
  channels: BroadcastChannel[];
  selectedChannelId?: string | null;
  loading: boolean;
  onSelect: (input: SelectBroadcastChannelInput) => Promise<void>;
  onRefresh?: () => void;
}

export function ChannelSelector({
  channels,
  selectedChannelId,
  loading,
  onSelect,
  onRefresh,
}: ChannelSelectorProps) {
  const { t } = useLocale();
  const [selected, setSelected] = useState(selectedChannelId ?? "");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const publicChannels = channels.filter((ch) => !ch.isPrivate);
  const selectedChannel = publicChannels.find((ch) => ch.id === selected);
  const hasChanged = selected !== (selectedChannelId ?? "");

  const handleSave = async () => {
    const channel = publicChannels.find((ch) => ch.id === selected);
    if (!channel) return;

    setSaving(true);
    try {
      await onSelect({ channelId: channel.id, channelName: channel.name });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-foreground">
        {t("integrations.channels.selectLabel")}
      </label>

      <div className="mt-1.5 flex items-center gap-2">
        <div className="relative flex-1" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            disabled={loading}
            className="flex w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted/50 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className={selectedChannel ? "text-foreground" : "text-muted-foreground"}>
              {loading
                ? t("integrations.channels.loading")
                : selectedChannel
                  ? `#${selectedChannel.name}`
                  : t("integrations.channels.placeholder")}
            </span>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
          </button>

          {open && !loading && (
            <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-border bg-card shadow-lg ring-1 ring-black/5 dark:ring-white/5">
              {publicChannels.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  {t("integrations.channels.noChannels")}
                </div>
              ) : (
                publicChannels.map((channel) => (
                  <button
                    key={channel.id}
                    type="button"
                    onClick={() => {
                      setSelected(channel.id);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center justify-between px-3 py-2 text-sm transition-colors hover:bg-muted ${
                      selected === channel.id
                        ? "bg-primary/10 text-primary"
                        : "text-foreground"
                    }`}
                  >
                    <span>#{channel.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {t("integrations.channels.members", { count: channel.memberCount })}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="rounded-md border border-border px-2.5 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            title={t("integrations.channels.refreshTitle")}
          >
            ↻
          </button>
        )}
      </div>

      {hasChanged && selected && (
        <div className="mt-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="h-8 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                {t("integrations.channels.saving")}
              </span>
            ) : (
              t("integrations.channels.saveChannel")
            )}
          </button>
        </div>
      )}
    </div>
  );
}
